// SPDX-License-Identifier: AGPL-3.0-or-later

import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {http} from '@app/features/platform/transport/RestTransport';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import type {Activity} from '@fluxer/schema/src/domains/gateway/ActivitySchemas';
import {ActivityType} from '@fluxer/schema/src/domains/gateway/ActivitySchemas';
import {useLingui} from '@lingui/react/macro';
import {
	AppleLogoIcon,
	BroadcastIcon,
	DotsThreeIcon,
	GameControllerIcon,
	HeadphonesIcon,
	MusicNotesIcon,
	SpotifyLogoIcon,
	TrophyIcon,
} from '@phosphor-icons/react';
import React, {useEffect, useState} from 'react';
import {getPresenceHeaderTitle} from '@app/features/presence/utils/PresenceLocales';
import styles from './RichPresenceCard.module.css';

interface RichPresenceCardProps {
	activity: Activity;
}

function formatDuration(ms: number, alwaysPadMinutes = false): string {
	const totalSeconds = Math.max(0, Math.floor(ms / 1000));
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;

	const paddedMin = hours > 0 || alwaysPadMinutes ? String(minutes).padStart(2, '0') : String(minutes);
	const paddedSec = String(seconds).padStart(2, '0');

	if (hours > 0) {
		return `${hours}:${paddedMin}:${paddedSec}`;
	}
	return `${paddedMin}:${paddedSec}`;
}

const VSCODE_ORIGINAL_LOGO = 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vscode/vscode-original.svg';

export const RichPresenceCard: React.FC<RichPresenceCardProps> = ({activity}) => {
	const {i18n} = useLingui();
	const [now, setNow] = useState(Date.now());
	const [fetchedArt, setFetchedArt] = useState<string | null>(null);
	const [fetchedDurationMs, setFetchedDurationMs] = useState<number>(0);
	const [appInfo, setAppInfo] = useState<{name: string; avatarUrl: string | null} | null>(null);

	// Fetch public application info (name and bot avatar) if application_id is provided
	useEffect(() => {
		if (!activity.application_id) {
			setAppInfo(null);
			return;
		}

		let cancelled = false;
		http.get<{name: string; icon: string | null; bot?: {id: string; avatar: string | null} | null}>(
			`/applications/${activity.application_id}/public`
		)
			.then((res) => {
				if (cancelled || !res.body) return;
				const data = res.body;
				let avatarUrl: string | null = null;
				if (data.bot?.id) {
					avatarUrl = AvatarUtils.getUserAvatarURL({id: data.bot.id, avatar: data.bot.avatar}, false);
				}
				setAppInfo({
					name: data.name,
					avatarUrl,
				});
			})
			.catch(() => {});

		return () => {
			cancelled = true;
		};
	}, [activity.application_id]);

	useEffect(() => {
		if (!activity.timestamps?.start && !activity.timestamps?.end) return;
		const interval = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(interval);
	}, [activity.timestamps?.start, activity.timestamps?.end]);

	const lowerName = activity.name.toLowerCase();

	const isVsCode =
		lowerName.includes('visual studio code') ||
		lowerName === 'code' ||
		activity.application_id === '383226320970055681';

	const isSpotify = lowerName.includes('spotify');
	const isAppleMusic = lowerName.includes('apple music') || lowerName.includes('itunes');
	const isYoutubeMusic = lowerName.includes('youtube music') || lowerName.includes('yt music');

	const isListening =
		activity.type === ActivityType.Listening ||
		isSpotify ||
		isAppleMusic ||
		isYoutubeMusic;

	// Client-side fallback: if music activity has no album art or no end timestamp, fetch from iTunes Search API
	useEffect(() => {
		if (!isListening || !activity.details) {
			setFetchedArt(null);
			setFetchedDurationMs(0);
			return;
		}
		const hasExistingImage = Boolean(activity.assets?.large_image);
		const hasExistingEnd = Boolean(
			activity.timestamps?.start &&
				activity.timestamps?.end &&
				activity.timestamps.end > activity.timestamps.start,
		);
		if (hasExistingImage && hasExistingEnd) {
			return;
		}

		let cancelled = false;
		const rawArtist = (activity.state || '').split(' - ')[0].split(',')[0].split('&')[0].trim();
		const q = encodeURIComponent(`${activity.details} ${rawArtist}`.trim());

		fetch(`https://itunes.apple.com/search?term=${q}&country=TR&entity=song&limit=1`)
			.then((r) => (r.ok ? r.json() : null))
			.then((data) => {
				if (cancelled || !data?.results?.[0]) return;
				const first = data.results[0];
				if (first.artworkUrl100) {
					setFetchedArt(first.artworkUrl100.replace('100x100bb', '600x600bb'));
				}
				if (typeof first.trackTimeMillis === 'number' && first.trackTimeMillis > 0) {
					setFetchedDurationMs(first.trackTimeMillis);
				}
			})
			.catch(() => {});

		return () => {
			cancelled = true;
		};
	}, [isListening, activity.details, activity.state, activity.assets?.large_image, activity.timestamps?.start, activity.timestamps?.end]);

	const effectiveType = isListening ? ActivityType.Listening : activity.type;
	const serviceName = isListening ? activity.name : undefined;

	const headerTitle = getPresenceHeaderTitle(effectiveType, i18n.locale, serviceName);

	const renderServiceBadgeIcon = () => {
		if (isSpotify) {
			return <SpotifyLogoIcon size={16} weight="fill" className={styles.serviceHeaderIcon} />;
		}
		if (isAppleMusic) {
			return <AppleLogoIcon size={15} weight="fill" className={styles.serviceHeaderIcon} />;
		}
		if (isListening) {
			return <MusicNotesIcon size={15} weight="fill" className={styles.serviceHeaderIcon} />;
		}
		return null;
	};

	const renderFallbackIcon = () => {
		if (isVsCode) {
			return (
				<div className={styles.vsCodeKeyboardBox}>
					<svg
						width="34"
						height="34"
						viewBox="0 0 24 24"
						fill="none"
						stroke="#00bcd4"
						strokeWidth="1.75"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<path d="M12 2v4M12 2a2 2 0 0 1 2 2v0a2 2 0 0 0 2 2h1a2 2 0 0 1 2 2v1" />
						<rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
						<line x1="6" y1="11" x2="6.01" y2="11" />
						<line x1="10" y1="11" x2="10.01" y2="11" />
						<line x1="14" y1="11" x2="14.01" y2="11" />
						<line x1="18" y1="11" x2="18.01" y2="11" />
						<line x1="6" y1="14" x2="6.01" y2="14" />
						<line x1="10" y1="14" x2="10.01" y2="14" />
						<line x1="14" y1="14" x2="14.01" y2="14" />
						<line x1="18" y1="14" x2="18.01" y2="14" />
						<line x1="7" y1="17" x2="17" y2="17" />
					</svg>
				</div>
			);
		}

		if (isSpotify) {
			return (
				<div className={styles.spotifyFallbackBox}>
					<SpotifyLogoIcon size={36} weight="fill" />
				</div>
			);
		}

		if (isAppleMusic) {
			return (
				<div className={styles.appleMusicFallbackBox}>
					<MusicNotesIcon size={34} weight="fill" />
				</div>
			);
		}

		switch (effectiveType) {
			case ActivityType.Listening:
				return <HeadphonesIcon size={34} weight="fill" />;
			case ActivityType.Streaming:
				return <BroadcastIcon size={34} weight="fill" />;
			case ActivityType.Competing:
				return <TrophyIcon size={34} weight="fill" />;
			default:
				return <GameControllerIcon size={34} weight="fill" />;
		}
	};

	const startMs = activity.timestamps?.start;
	const endMs =
		activity.timestamps?.end && startMs && activity.timestamps.end > startMs
			? activity.timestamps.end
			: startMs && fetchedDurationMs > 0
				? startMs + fetchedDurationMs
				: startMs && isListening
					? startMs + 210 * 1000
					: activity.timestamps?.end;

	const hasProgressBar =
		isListening &&
		typeof startMs === 'number' &&
		typeof endMs === 'number' &&
		endMs > startMs;

	let progressPercent = 0;
	let currentProgressStr = '00:00';
	let totalDurationStr = '00:00';

	if (hasProgressBar && startMs && endMs) {
		const totalMs = endMs - startMs;
		const elapsedMs = Math.min(totalMs, Math.max(0, now - startMs));
		progressPercent = totalMs > 0 ? Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100)) : 0;
		currentProgressStr = formatDuration(elapsedMs, true);
		totalDurationStr = formatDuration(totalMs, true);
	}

	let timerText: string | null = null;
	if (!isListening && !hasProgressBar) {
		if (activity.timestamps?.end) {
			const remaining = activity.timestamps.end - now;
			timerText = formatDuration(remaining);
		} else if (activity.timestamps?.start) {
			const elapsed = now - activity.timestamps.start;
			timerText = formatDuration(elapsed);
		}
	}

	const [imageFailed, setImageFailed] = useState(false);

	const rawLarge = activity.assets?.large_image || fetchedArt || appInfo?.avatarUrl;

	useEffect(() => {
		setImageFailed(false);
	}, [rawLarge]);

	const resolveImageUrl = (raw?: string | null): string | null => {
		if (!raw) return null;
		let url: string | null = null;
		if (raw.startsWith('http://') || raw.startsWith('https://')) {
			url = raw;
		} else if (raw.startsWith('spotify:')) {
			const hash = raw.slice('spotify:'.length);
			url = `https://i.scdn.co/image/${hash}`;
		} else if (raw.startsWith('mp:external/')) {
			const httpsIdx = raw.indexOf('/https/');
			if (httpsIdx !== -1) {
				url = `https://${raw.slice(httpsIdx + 7)}`;
			}
		}
		if (!url) return null;
		if (typeof window !== 'undefined' && (window as any).electron) {
			return url;
		}
		return `/api/v1/proxy/image?url=${encodeURIComponent(url)}`;
	};

	const largeImageUrl = resolveImageUrl(rawLarge);

	const smallImageUrl =
		resolveImageUrl(activity.assets?.small_image) ||
		(isVsCode ? VSCODE_ORIGINAL_LOGO : null);

	const resolvedAppName =
		activity.name && !activity.name.startsWith('App (') && activity.name !== 'Game'
			? activity.name
			: appInfo?.name || activity.name;
	const primaryTitle = isListening && activity.details ? activity.details : resolvedAppName;
	const secondarySubtitle = isListening
		? activity.state || activity.assets?.large_text
		: isVsCode && (!activity.details || activity.details === 'In-game')
			? 'Idling'
			: activity.details;
	const tertiaryLine = isListening ? null : activity.state;

	return (
		<div className={styles.container} data-flx="user.profile.rich-presence-card.container">
			<div className={styles.card} data-flx="user.profile.rich-presence-card.card">
				<div className={styles.headerRow} data-flx="user.profile.rich-presence-card.header-row">
					<div className={styles.headerTitleGroup} data-flx="user.profile.rich-presence-card.header-title">
						<span>{headerTitle}</span>
						{renderServiceBadgeIcon()}
					</div>
					<div className={styles.headerMenuDots} data-flx="user.profile.rich-presence-card.menu-dots">
						<DotsThreeIcon size={20} weight="bold" />
					</div>
				</div>

				<div className={styles.bodyRow} data-flx="user.profile.rich-presence-card.body-row">
					<div className={styles.assetWrapper} data-flx="user.profile.rich-presence-card.asset-wrapper">
						{largeImageUrl && !imageFailed ? (
							activity.assets?.large_text ? (
								<Tooltip text={activity.assets.large_text} data-flx="user.profile.rich-presence-card.large-tooltip">
									<img
										src={largeImageUrl}
										alt={activity.assets.large_text || activity.name}
										className={styles.largeImage}
										onError={() => setImageFailed(true)}
										data-flx="user.profile.rich-presence-card.large-image"
									/>
								</Tooltip>
							) : (
								<img
									src={largeImageUrl}
									alt={activity.name}
									className={styles.largeImage}
									onError={() => setImageFailed(true)}
									data-flx="user.profile.rich-presence-card.large-image"
								/>
							)
						) : (
							<div className={styles.largeImageFallback} data-flx="user.profile.rich-presence-card.fallback-icon">
								{renderFallbackIcon()}
							</div>
						)}

						{smallImageUrl && (
							activity.assets?.small_text ? (
								<Tooltip text={activity.assets.small_text} data-flx="user.profile.rich-presence-card.small-tooltip">
									<img
										src={smallImageUrl}
										alt={activity.assets.small_text}
										className={styles.smallImage}
										data-flx="user.profile.rich-presence-card.small-image"
									/>
								</Tooltip>
							) : (
								<img
									src={smallImageUrl}
									alt="Presence status"
									className={styles.smallImage}
									data-flx="user.profile.rich-presence-card.small-image"
								/>
							)
						)}
					</div>

					<div className={styles.detailsCol} data-flx="user.profile.rich-presence-card.details-col">
						<div className={styles.activityName} data-flx="user.profile.rich-presence-card.activity-name">
							{primaryTitle}
						</div>
						{secondarySubtitle && (
							<div className={styles.detailLine} data-flx="user.profile.rich-presence-card.detail-line">
								{secondarySubtitle}
							</div>
						)}
						{tertiaryLine && (
							<div className={styles.stateLine} data-flx="user.profile.rich-presence-card.state-line">
								{tertiaryLine}
							</div>
						)}
						{hasProgressBar ? (
							<div className={styles.musicProgressRow} data-flx="user.profile.rich-presence-card.music-progress">
								<span className={styles.musicTimeText}>{currentProgressStr}</span>
								<div className={styles.musicProgressBarTrack}>
									<div
										className={styles.musicProgressBarFill}
										style={{width: `${progressPercent}%`}}
									/>
								</div>
								<span className={styles.musicTimeText}>{totalDurationStr}</span>
							</div>
						) : (
							timerText && (
								<div className={styles.timerLine} data-flx="user.profile.rich-presence-card.timer-line">
									<GameControllerIcon size={14} weight="fill" className={styles.timerIcon} />
									<span>{timerText}</span>
								</div>
							)
						)}
					</div>
				</div>

				{activity.buttons && activity.buttons.length > 0 && (
					<div className={styles.buttonRow} data-flx="user.profile.rich-presence-card.button-row">
						{activity.buttons.map((btn, idx) => (
							<a
								key={idx}
								href={btn.url}
								target="_blank"
								rel="noopener noreferrer"
								className={styles.actionButton}
								data-flx={`user.profile.rich-presence-card.action-button--${idx}`}
							>
								{btn.label}
							</a>
						))}
					</div>
				)}
			</div>
		</div>
	);
};
